import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useFacilityData, useSlotConfiguration } from '../../hooks/booking/useBookingContextSlices';
import { facilitiesAPI } from '../../services/api';
import WizardProgress, { SERVICE_WIZARD_STEPS } from '../../components/WizardProgress';

const ConfigureService = ({ onComplete, initialData, dateRange }) => {
  const { facility, loading: facilityLoading } = useFacilityData();
  const { slotConfiguration } = useSlotConfiguration();
  const availableServices = (() => {
    const fromFacility = Array.isArray(facility?.services) ? facility.services : [];
    const fromConfig = Array.isArray(slotConfiguration?.services)
      ? slotConfiguration.services
          .map((cfg) => cfg?.service)
          .filter(Boolean)
      : [];

    const byId = new Map();
    [...fromFacility, ...fromConfig].forEach((service) => {
      const key = service?.id != null
        ? `id:${service.id}`
        : `name:${service?.name || ''}|type:${service?.type || ''}`;
      if (!byId.has(key)) {
        byId.set(key, service);
      }
    });

    return Array.from(byId.values());
  })();
  const [serviceData, setServiceData] = useState(
    initialData || {
      name: '',
      description: '',
      category: 'team_sports', // 'team_sports' or 'individual_sports'
      id: null
    }
  );
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load existing service configuration from database on mount
  useEffect(() => {
    const loadExistingConfiguration = async () => {
      // Check if there's existing configuration in slotConfiguration
      if (slotConfiguration?.services && slotConfiguration.services.length > 0) {
        // If initialData is provided, use it
        if (initialData && initialData.name) {
          return;
        }
        
        // Otherwise, try to load from the first configured service
        const firstConfig = slotConfiguration.services[0];
        if (firstConfig?.service) {
          setServiceData({
            name: firstConfig.service.name || '',
            description: firstConfig.service.description || '',
            category: firstConfig.service.category || 'team_sports',
            id: firstConfig.service.id || null
          });
        }
      }
      
      // Also try to load from facility services if a service is selected
      if (serviceData.name && availableServices.length > 0) {
        const selectedService = availableServices.find(s => s.name === serviceData.name);
        if (selectedService) {
          setServiceData(prev => ({
            ...prev,
            id: selectedService.id,
            description: selectedService.description || prev.description,
            category: selectedService.category || prev.category
          }));
        }
      }
    };

    loadExistingConfiguration();
  }, [slotConfiguration, facility, initialData]);

  // Update service data when name is selected
  useEffect(() => {
    if (serviceData.name && availableServices.length > 0) {
      const selectedService = availableServices.find(s => s.name === serviceData.name);
      if (selectedService) {
        setServiceData(prev => ({
          ...prev,
          id: selectedService.id,
          description: selectedService.description || prev.description,
          category: selectedService.category || prev.category
        }));
      }
    }
  }, [serviceData.name, facility, slotConfiguration]);

  const validate = () => {
    const newErrors = {};
    
    if (!serviceData.name.trim()) {
      newErrors.name = 'Service name is required';
    }
    
    if (!serviceData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Find the service ID from facility services
      const selectedService = availableServices.find(s => s.name === serviceData.name);
      if (!selectedService || !selectedService.id) {
        throw new Error('Service not found. Please select a valid service.');
      }

      // Save service configuration to database
      const updateData = {
        category: serviceData.category,
        pricing_model: serviceData.category === 'individual_sports' ? 'subscription' : 'slot',
        description: serviceData.description
      };

      console.log('💾 Saving service configuration to database:', {
        serviceId: selectedService.id,
        serviceName: serviceData.name,
        ...updateData
      });

      const response = await facilitiesAPI.updateService(selectedService.id, updateData);

      if (response && response.success) {
        console.log('✅ Service configuration saved to database:', response.data);
        setSaveSuccess(true);
        
        // Pass the updated service data with ID to onComplete
        const updatedServiceData = {
          ...serviceData,
          id: selectedService.id,
          type: selectedService.type || 'cricket_match'
        };
        
        // Call onComplete after a brief delay to show success message
        setTimeout(() => {
          onComplete(updatedServiceData);
        }, 500);
      } else {
        throw new Error(response?.message || 'Failed to save service configuration');
      }
    } catch (error) {
      console.error('❌ Error saving service configuration:', error);
      setSaveError(error.message || 'Failed to save service configuration. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="max-w-4xl mx-auto">
      <WizardProgress steps={SERVICE_WIZARD_STEPS} currentStep={2} />
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Configure Service</h2>
        <p className="text-gray-600">
          Define your service details. Slot configuration will be done in the next step.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Service Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name of Service *
          </label>
          <select
            value={serviceData.name}
            onChange={(e) => {
              const selectedService = facility?.services?.find(s => s.name === e.target.value);
              setServiceData(prev => ({ 
                ...prev, 
                name: e.target.value,
                description: selectedService?.description || prev.description
              }));
              setErrors(prev => ({ ...prev, name: '' }));
            }}
            className="input-field w-full"
            required
          >
            <option value="">Select a service...</option>
            {availableServices.map((service, index) => (
              <option key={service.id || index} value={service.name}>
                {service.name}
              </option>
            ))}
          </select>
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
          {!facilityLoading && availableServices.length === 0 && (
            <p className="text-yellow-600 text-sm mt-1">
              No services available. Please configure facility services first.
            </p>
          )}
        </div>

        {/* Pricing model (§8 step 3): how the service is priced/booked.
            Stored as category for back-compat; the server derives pricing_model. */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            How is this booked? *
          </label>
          <select
            value={serviceData.category === 'individual_sports' ? 'subscription' : 'slot'}
            onChange={(e) => {
              const category = e.target.value === 'subscription' ? 'individual_sports' : 'team_sports';
              setServiceData(prev => ({ ...prev, category }));
              setErrors(prev => ({ ...prev, category: '' }));
            }}
            className="input-field w-full"
            required
          >
            <option value="slot">Slot-based (time slots)</option>
            <option value="subscription">Subscription (membership)</option>
          </select>
          {errors.category && (
            <p className="text-red-500 text-sm mt-1">{errors.category}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Slot-based: booked by time slot (e.g. cricket match, nets). Subscription: recurring membership priced monthly/weekly/per-day/hourly (e.g. coaching).
          </p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What is offered in this service? (Description) *
          </label>
          <textarea
            value={serviceData.description}
            onChange={(e) => {
              setServiceData(prev => ({ ...prev, description: e.target.value }));
              setErrors(prev => ({ ...prev, description: '' }));
            }}
            rows="4"
            className="input-field w-full"
            placeholder="Describe what this service offers to customers..."
            required
          />
          {errors.description && (
            <p className="text-red-500 text-sm mt-1">{errors.description}</p>
          )}
        </div>

        {/* Save Error */}
        {saveError && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-sm text-red-800">
            {saveError}
          </div>
        )}

        {/* Save Success */}
        {saveSuccess && (
          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3 text-sm text-green-800">
            ✅ Service configuration saved to database successfully!
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <span>Saving...</span>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              </>
            ) : (
              <>
                <span>Continue to Slot Configuration</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfigureService;

